import React, { useContext, useEffect } from 'react';
import {
	IEditFormForClientManagerContext,
	EditFormForClientManagerContext,
} from '../../EditFormForClientManagerState/editFormForClientManagerContext';
import {
	IWithAdditionalValidationContext,
	WithAdditionalValidationContext,
} from 'components/WithAdditionalValidation/withAdditionalValidationContext';
import { IMccCard } from 'types';
import { preventDefaultForEnterKey } from 'utils/helpers';
import { Form } from 'antd';
import BtnBlock from './components/BtnBlock';
import UseCustomerMenuVersionCheckBox from './components/UseCustomerMenuVersionCheckBox';
import UseCustomerContentVersionCheckBox from './components/UseCustomerContentVersionCheckBox';
import MenuVersionField from './components/MenuVersionField';
import ContentVersionField from './components/ContentVersionField';
import UseRestaurantMenuVersionCheckBox from './components/UseRestaurantMenuVersionCheckBox';
import UseRestaurantContentVersionCheckBox from './components/UseRestaurantContentVersionCheckBox';
import Text from 'components/Text/Text';
import classNames from 'classnames';
import style from './InputFields.module.scss';

export const InputFields: React.FC = React.memo(() => {
	const { setMainFormValidationFail, additionalValidationErrors } = useContext<IWithAdditionalValidationContext>(
		WithAdditionalValidationContext,
	);

	const {
		toggleUseMenuVersion,
		toggleUseContentVersion,
		changeRestaurantMenuVersion,
		changeRestaurantContentVersion,
		setDataForSubmit,
		changeTypeOfModalWindow,
		fullRestaurantInfo,
		useMenuVersion,
		useContentVersion,
		restaurantMenuVersion,
		restaurantContentVersion,
	} = useContext<IEditFormForClientManagerContext>(EditFormForClientManagerContext);
	const [form] = Form.useForm();
	const versionHeadingClasses = classNames(style.row, style.flex1, style.versionHeading);
	const rowWithFlex1Classes = classNames(style.row, style.flex1);

	useEffect(() => {
		form.setFieldsValue({ useMenuVersion });
		form.setFieldsValue({ useContentVersion });
		form.setFieldsValue({ restaurantMenuVersion });
		form.setFieldsValue({ restaurantContentVersion });
	}, [useMenuVersion, useContentVersion, restaurantMenuVersion, restaurantContentVersion]);

	const submit = () => {
		setMainFormValidationFail(false);

		if (fullRestaurantInfo) {
			const finalMccCards = () => {
				const result: Pick<IMccCard, 'brand' | 'mcc'>[] = [];

				if (
					fullRestaurantInfo.merchantCategoryCode?.mccCards[0]?.brand &&
					fullRestaurantInfo.merchantCategoryCode?.mccCards[0]?.mcc
				) {
					result.push({
						brand: fullRestaurantInfo.merchantCategoryCode?.mccCards[0].brand,
						mcc: fullRestaurantInfo.merchantCategoryCode?.mccCards[0].mcc,
					});
				}

				fullRestaurantInfo.merchantCategoryCode?.mccCards.slice(1).forEach((el) => {
					if (el.brand && el.mcc) {
						result.push({ brand: el.brand, mcc: el.mcc });
					}
				});

				return result;
			};

			setDataForSubmit({
				restaurantId: fullRestaurantInfo.id,
				name: fullRestaurantInfo.name,
				shortName: fullRestaurantInfo.shortName,
				address: fullRestaurantInfo.address,
				customerId: fullRestaurantInfo.customerId,
				cityId: fullRestaurantInfo.city.id,
				countryId: fullRestaurantInfo.countryId,
				defaultLanguageId: fullRestaurantInfo.defaultLanguageId,
				restaurantType: fullRestaurantInfo.restaurantType,
				isoCurrencySymbol: fullRestaurantInfo.isoCurrencySymbol,
				masterPhoneNumber: fullRestaurantInfo.masterPhoneNumber,
				email: fullRestaurantInfo.email,
				code: fullRestaurantInfo.code,
				additionalApplications: fullRestaurantInfo.additionalApplications,
				vivaWalletIsv: fullRestaurantInfo.vivaWalletIsv,
				supplier: fullRestaurantInfo.supplier,
				isOpenPriceServiceCharge: fullRestaurantInfo.isOpenPriceServiceCharge,
				openPricePercent: fullRestaurantInfo.openPricePercent,
				priceType: fullRestaurantInfo.priceType,
				isUsedCustomerPriceType: fullRestaurantInfo.isUsedCustomerPriceType,
				loyalty: fullRestaurantInfo.loyalty || 0,
				posSystemId: fullRestaurantInfo.posSystemId,
				isUsedCustomerTips: fullRestaurantInfo.isUsedCustomerTips,
				defaultTips: fullRestaurantInfo.defaultTips,
				tipsCommissionPercent: fullRestaurantInfo.tipsCommissionPercent,
				tipsCommissionMaxValue: fullRestaurantInfo.tipsCommissionMaxValue,
				commissionTariff: fullRestaurantInfo.commissionTariff,
				isShowLoyaltyProgramButtons: fullRestaurantInfo.isShowLoyaltyProgramButtons,
				merchantCategoryCode: {
					baseMcc: fullRestaurantInfo.merchantCategoryCode?.baseMcc || 0,
					mccCards: finalMccCards(),
				},
				useCustomerMcc: fullRestaurantInfo.useCustomerMcc,
				downloadAppAndroidUrl: fullRestaurantInfo.downloadAppAndroidUrl,
				downloadAppIosUrl: fullRestaurantInfo.downloadAppIosUrl,
				cardAcceptorId: fullRestaurantInfo.cardAcceptorId,
				cardAcceptorName: fullRestaurantInfo.cardAcceptorName,
				terminalId: fullRestaurantInfo.terminalId,
				terminal: fullRestaurantInfo.terminal,
				timeBlock: fullRestaurantInfo.timeBlock,
				restrictionQrValue: fullRestaurantInfo.restrictionQrValue,
				usePosPhotos: fullRestaurantInfo.usePosPhotos,
				syncStopList: fullRestaurantInfo.syncStopList,
				useCustomerStopListSyncInterval: fullRestaurantInfo.useCustomerStopListSyncInterval,
				stopListSyncIntervalMinutes: fullRestaurantInfo.stopListSyncIntervalMinutes,
				updateOfTheLauncher: fullRestaurantInfo.updateOfTheLauncher,
				launcherSettingFileId: '',
				restaurantsLanguages: fullRestaurantInfo.restaurantLanguages.map((el) => el.languageId),
				useTlsCertificate: fullRestaurantInfo.useTlsCertificate,
				tlsCertificateLevel: fullRestaurantInfo.tlsCertificateLevel,
				tlsCertificateName: fullRestaurantInfo.tlsCertificateName,
				tlsCertificateFileId: '',
				tlsCertificateFileHash: null,
				vatId: fullRestaurantInfo.vatId,
				kmVatId: fullRestaurantInfo.kmVatId,
				bpaSetId: fullRestaurantInfo.bpaSet?.id || null,
				isApplicableForVat: fullRestaurantInfo.isApplicableForVat,
				sendDeviceUnavailabilityAlerts: fullRestaurantInfo.sendDeviceUnavailabilityAlerts,
				paymentTypeId: fullRestaurantInfo.paymentTypeId,
				useMenuVersion,
				useContentVersion,
				menuVersion: restaurantMenuVersion || null,
				contentVersion: restaurantContentVersion || null,
				extraPaymentTypes: fullRestaurantInfo.extraPaymentTypes,
				isCharityEnabled: fullRestaurantInfo.isCharityEnabled,
				charityName: fullRestaurantInfo.charityName,
				charityLogoFileId: fullRestaurantInfo.charityLogoPath ? '' : null,
				charityAmounts: fullRestaurantInfo.charityAmounts,
			});
		}

		additionalValidationErrors.length
			? setMainFormValidationFail(true)
			: changeTypeOfModalWindow('toConfirmSubmit');
	};

	const handleFormValidationFail = () => {
		setMainFormValidationFail(true);
	};

	return (
		<div className={style.container}>
			<Form
				form={form}
				name="basic"
				onFinish={submit}
				onKeyPress={preventDefaultForEnterKey}
				onFinishFailed={handleFormValidationFail}
				layout="vertical">
				<div className={style.fieldsWrapper}>
					<Text
						className={style.menuContentVersionHeading}
						translationNameSpace="form"
						translationKey="menuContentVersionHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<div className={versionHeadingClasses}>
						<Text
							translationNameSpace="form"
							translationKey="menuVersionHeading"
							theme="secondary"
							size="m"
							weight="bold"
							display="inline"
						/>
						<Text
							translationNameSpace="form"
							translationKey="contentVersionHeading"
							theme="secondary"
							size="m"
							weight="bold"
							display="inline"
						/>
					</div>
					<div className={rowWithFlex1Classes}>
						<UseCustomerMenuVersionCheckBox
							toggleUseMenuVersion={toggleUseMenuVersion}
							useMenuVersion={useMenuVersion}
						/>
						<UseCustomerContentVersionCheckBox
							toggleUseContentVersion={toggleUseContentVersion}
							useContentVersion={useContentVersion}
						/>
					</div>
					<div className={style.row}>
						<MenuVersionField
							changeMenuVersion={changeRestaurantMenuVersion}
							useMenuVersion={useMenuVersion}
						/>
						<ContentVersionField
							changeContentVersion={changeRestaurantContentVersion}
							useContentVersion={useContentVersion}
						/>
					</div>
					<div className={rowWithFlex1Classes}>
						<UseRestaurantMenuVersionCheckBox
							toggleUseMenuVersion={toggleUseMenuVersion}
							useMenuVersion={useMenuVersion}
						/>
						<UseRestaurantContentVersionCheckBox
							toggleUseContentVersion={toggleUseContentVersion}
							useContentVersion={useContentVersion}
						/>
					</div>
				</div>
				<BtnBlock />
			</Form>
		</div>
	);
});
